import { ChevronDownIcon, CloudIcon, LaptopIcon, ServerIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Menu, MenuGroup, MenuGroupLabel, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { StatusDot } from "./DiagnosticsPanelPrimitives";
import {
  diagnosticsTargetTriggerLabel,
  type DiagnosticsTargetGroup,
  type DiagnosticsTargetKind,
  type DiagnosticsTargetOption,
} from "./DiagnosticsTargetPicker.logic";

const TARGET_ICONS: Readonly<Record<DiagnosticsTargetKind, typeof CloudIcon>> = {
  server: ServerIcon,
  cloud: CloudIcon,
  desktop: LaptopIcon,
};

function TargetIcon({
  kind,
  className,
}: {
  readonly kind: DiagnosticsTargetKind;
  className?: string;
}) {
  const Icon = TARGET_ICONS[kind];
  return <Icon aria-hidden="true" className={className} />;
}

export function DiagnosticsTargetPicker({
  groups,
  active,
  onSelect,
}: {
  readonly groups: ReadonlyArray<DiagnosticsTargetGroup>;
  readonly active: DiagnosticsTargetOption | null;
  readonly onSelect: (option: DiagnosticsTargetOption) => void;
}) {
  const empty = groups.length === 0;
  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            aria-label="Diagnostics target"
            variant="outline"
            size="sm"
            className="max-w-64 justify-between gap-2"
            disabled={empty}
          />
        }
      >
        <span className="flex min-w-0 items-center gap-2">
          {active ? (
            <TargetIcon kind={active.kind} className="size-4 shrink-0 text-muted-foreground" />
          ) : null}
          <span className="truncate">
            {empty ? "No targets available" : diagnosticsTargetTriggerLabel(active)}
          </span>
        </span>
        <ChevronDownIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      </MenuTrigger>
      <MenuPopup align="start" className="min-w-64">
        {groups.map((group) => (
          <MenuGroup key={group.id}>
            <MenuGroupLabel>{group.label}</MenuGroupLabel>
            {group.options.map((option) => (
              <MenuItem
                key={option.key}
                onClick={() => onSelect(option)}
                className={cn(active?.key === option.key ? "bg-accent/60" : null)}
              >
                <TargetIcon kind={option.kind} className="size-4 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                  <StatusDot tone={option.tone} />
                  {option.detail}
                </span>
              </MenuItem>
            ))}
          </MenuGroup>
        ))}
      </MenuPopup>
    </Menu>
  );
}
